'use client';

import { buttonVariants } from './ui/button';
import { Heading } from '@/components/ui/heading';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import { Link } from '@/i18n/navigation';
import { Sparkles, ScanLine, ArrowRight } from 'lucide-react';
import Container from './ui/container';

export function Cta() {
	const t = useTranslations('contentElements.feedbackCta');

	return (
		<Container>
			<div className="sm:px-6 lg:px-8 space-y-10">
				{/* Final High-Converting Conversion Banner */}
				<motion.div
					className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-[#0f766e] text-white p-8 sm:p-14 text-center relative overflow-hidden shadow-xl"
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
				>
					{/* Decorative blur glows */}
					<div className="absolute -top-24 -right-24 size-64 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />
					<div className="absolute -bottom-24 -left-24 size-64 bg-teal-500/20 rounded-full blur-3xl pointer-events-none" />

					<div className="relative z-10 max-w-2xl mx-auto">
						<div className="text-emerald-400 text-sm font-bold uppercase tracking-wider mb-4">
							<span>100% Free • Unlimited Scans</span>
						</div>
						<h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight mb-4 text-white">
							Create Your Free QR Code in Seconds
						</h2>
						<p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-8">
							Generate unlimited static and dynamic codes, design mobile landing pages, and track
							real-time analytics with zero subscription traps and no credit card required.
						</p>
						<div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 max-w-sm sm:max-w-none mx-auto">
							<Link
								href="/create"
								className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0f766e] hover:bg-[#115e59] active:bg-[#134e4a] text-white font-semibold text-sm px-6 py-3.5 rounded-2xl shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
							>
								<Sparkles className="size-4" />
								<span>Create Free QR Code</span>
								<ArrowRight className="size-4 ml-0.5" />
							</Link>
							<Link
								href="/scanner"
								className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-800/90 hover:bg-slate-700 text-slate-200 hover:text-white font-semibold text-sm px-5 py-3.5 rounded-2xl border border-slate-700 shadow-2xs transition-all active:scale-[0.98]"
							>
								<ScanLine className="size-4 text-emerald-400" />
								<span>Scan Barcode / QR</span>
							</Link>
						</div>
					</div>
				</motion.div>

				{/* Feedback & Support Card */}
				<motion.div
					className="max-w-5xl mx-auto p-px rounded-2xl bg-gradient-to-r from-slate-100 to-slate-200 border border-slate-200/80 shadow-xs"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.5, delay: 0.1 }}
				>
					<div className="flex flex-col items-center justify-center text-center py-10 px-5 xs:px-10 md:py-12 rounded-[15px] bg-white">
						<Heading as="h2" size="lg">
							{t('headline')}
						</Heading>
						<p className="text-slate-600 mt-2 text-sm sm:text-base max-w-xl">{t('subHeadline1')}</p>
						<div className="mt-6">
							<a
								href="mailto:support_qrcode@ekutulimited.com"
								className={buttonVariants({ size: 'default' })}
							>
								<EnvelopeIcon className="mr-2 h-4 w-4" />
								{t('emailBtn')}
							</a>
						</div>
					</div>
				</motion.div>
			</div>
		</Container>
	);
}
